
import React, { useState } from 'react';
import { UseFormReturn, ControllerRenderProps } from 'react-hook-form';
import type { BlogPostFormData } from '@/services/blog/types';
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, X, Image } from 'lucide-react';
import { useImageUpload } from '../hooks/useImageUpload';
import { toast } from 'sonner';

interface MediaFieldsProps {
  form: UseFormReturn<BlogPostFormData>;
}

export const MediaFields: React.FC<MediaFieldsProps> = ({ form }) => {
  const { uploadImage, deleteImage, checkBucketAccess, isUploading } = useImageUpload();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      setSelectedFile(file);

      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const imageUrl = await uploadImage(selectedFile);
      if (imageUrl) {
        form.setValue('featured_image', imageUrl);
        setSelectedFile(null);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    }
  };

  const handleTestBucket = async () => {
    const success = await checkBucketAccess();
    if (success) {
      toast.success('Storage bucket is ready!');
    } else {
      toast.error('Storage bucket access failed');
    }
  };

  const handleRemoveImage = async () => {
    const currentImageUrl = form.getValues('featured_image');
    if (currentImageUrl) {
      const success = await deleteImage(currentImageUrl);
      if (success) {
        form.setValue('featured_image', '');
      }
    }
  };

  const handleCancelSelection = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const currentImageUrl = form.watch('featured_image');

  return (
    <FormField
      control={form.control}
      name="featured_image"
      render={({ field }: { field: ControllerRenderProps<BlogPostFormData, 'featured_image'> }) => (
        <FormItem>
          <FormLabel>Featured Image</FormLabel>
          <FormDescription>
            Upload an image to use as the featured image for this blog post. Maximum file size: 5MB.
          </FormDescription>

          {/* Debug button - temporary for troubleshooting */}
          <div className="mb-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleTestBucket}
            >
              Test Storage Setup
            </Button>
          </div>

          {/* Current Image Display */}
          {currentImageUrl && !selectedFile && (
            <div className="space-y-2">
              <div className="relative inline-block">
                <img
                  src={currentImageUrl}
                  alt="Featured image"
                  className="max-w-xs max-h-48 rounded-lg border object-cover"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveImage}
                >
                  <X className="h-4 w-4 mr-1" />
                  Remove Image
                </Button>
              </div>
            </div>
          )}

          {/* File Selection Preview */}
          {selectedFile && previewUrl && (
            <div className="space-y-2">
              <div className="relative inline-block">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-w-xs max-h-48 rounded-lg border object-cover"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleUpload}
                  disabled={isUploading}
                  size="sm"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  {isUploading ? 'Uploading...' : 'Upload Image'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelSelection}
                  disabled={isUploading}
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* File Input */}
          {!currentImageUrl && !selectedFile && (
            <div className="space-y-2">
              <Label
                htmlFor="featured-image-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Image className="w-8 h-8 mb-2 text-gray-500 dark:text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    PNG, JPG, GIF up to 5MB
                  </p>
                </div>
              </Label>
              <Input
                id="featured-image-upload"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="sr-only"
              />
            </div>
          )}

          <FormControl>
            <Input type="hidden" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
